import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  PlusIcon,
  BriefcaseIcon,
  GlobeAltIcon,
  EyeIcon,
  CodeBracketIcon,
} from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import { toast } from "react-hot-toast";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { PMDialog } from "../../../../components/utils/PMDialog";
import { PMField } from "../../../../components/utils/PMField";
import PMSwitch from "../../../../components/utils/PMSwitch";
import { updateUrlWithException } from "../../../../components/Utils";
import {
  createJobOpening,
  fetchJobOpenings,
} from "../../../../api/jobOpenings";
import { JobOpeningJsonDialog } from "../../../../components/dashboard/hr/JobOpeningJsonDialog";
import JobOpeningContactPicker, {
  contactUserIdFrom,
} from "../../../../components/dashboard/hr/JobOpeningContactPicker";
import { useCompanyHasModule } from "../../../../routes/roleOnly";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700",
  published: "bg-emerald-50 text-emerald-800",
  closed: "bg-amber-50 text-amber-800",
  archived: "bg-slate-50 text-slate-500",
};

export const JobOpeningsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const websiteModuleActive = useCompanyHasModule("website");
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);

  const [controller, setController] = useState({
    page: searchParams.get("page") ? parseInt(searchParams.get("page"), 10) : 1,
    query: searchParams.get("query") || "",
    sort: searchParams.get("sort") || "DESC",
    sortBy: searchParams.get("sortBy") || "created_at",
    status: searchParams.get("status") || "",
    per_page: 25,
  });

  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        status: controller.status || undefined,
      },
      "filters"
    );
  }, [controller]);

  const load = useCallback(
    debounce(async (ctrl) => {
      try {
        setIsLoading(true);
        const response = await fetchJobOpenings(ctrl);
        if (response?.data) {
          setItems(response.data);
          setCount(response.count || response.data.length || 0);
        } else {
          setItems([]);
          setCount(0);
          if (response?.error) toast.error(t("hr.job_openings.load_failed"));
        }
      } catch (e) {
        toast.error(t("hr.job_openings.load_failed"));
      } finally {
        setIsLoading(false);
      }
    }, 250),
    [t]
  );

  useEffect(() => {
    load(controller);
    return () => load.cancel();
  }, [controller, load]);

  const handleCreate = async (values, helpers) => {
    const { contact_user, ...rest } = values;
    const response = await createJobOpening({
      ...rest,
      contact_user_id: contactUserIdFrom(contact_user) ?? rest.contact_user_id ?? null,
    });
    if (response?.errors || response?.error) {
      helpers.setErrors({
        submit: response.errors?.[0] || t("hr.job_openings.create_failed"),
      });
      toast.error(t("hr.job_openings.create_failed"));
      return;
    }
    toast.success(t("hr.job_openings.created"));
    setDialogOpen(false);
    helpers.resetForm();
    if (response?.id) {
      navigate(`/dashboard/hr/job-openings/${response.id}`);
    } else {
      load(controller);
    }
  };

  return (
    <>
      <DashboardHeader
        variant="list"
        title={t("hr.job_openings.title")}
        subtitle={t("hr.job_openings.subtitle")}
        listCount={count}
        actions={[
          {
            label: t("hr.job_openings.json.open_dialog"),
            icon: CodeBracketIcon,
            action: () => setJsonDialogOpen(true),
            hidden: false,
            menu: false,
          },
        ]}
        mainButton={{
          label: t("hr.job_openings.new"),
          icon: PlusIcon,
          action: () => setDialogOpen(true),
        }}
      >
        <div className="p-3 space-y-4 md:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <ModuleFilter
            disabled={isLoading}
            onFiltersChange={(filters) =>
              setController((prev) => ({
                ...prev,
                page: 1,
                query: filters.query !== undefined ? filters.query : prev.query,
              }))
            }
            searchPlaceholder={t("hr.job_openings.search_placeholder")}
            hideStatusFilter={true}
          />
          <select
            className="h-[46px] rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800"
            value={controller.status}
            onChange={(e) =>
              setController((prev) => ({
                ...prev,
                page: 1,
                status: e.target.value,
              }))
            }
          >
            <option value="">{t("hr.common.all_statuses")}</option>
            <option value="draft">{t("hr.job_openings.statuses.draft")}</option>
            <option value="published">
              {t("hr.job_openings.statuses.published")}
            </option>
            <option value="closed">{t("hr.job_openings.statuses.closed")}</option>
            <option value="archived">
              {t("hr.job_openings.statuses.archived")}
            </option>
          </select>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.07)] md:rounded-[18px]">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">
                    {t("hr.job_openings.fields.title")}
                  </th>
                  <th className="px-4 py-3">
                    {t("hr.job_openings.fields.location")}
                  </th>
                  <th className="px-4 py-3">{t("hr.job_openings.status")}</th>
                  <th className="px-4 py-3">
                    {t("hr.job_openings.fields.deadline")}
                  </th>
                  <th className="px-4 py-3">
                    {t("hr.job_openings.candidates")}
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-10 text-center text-slate-500"
                    >
                      {t("hr.common.loading")}
                    </td>
                  </tr>
                )}
                {!isLoading && items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-14 text-center">
                      <BriefcaseIcon className="mx-auto h-10 w-10 text-slate-300" />
                      <p className="mt-3 text-base font-semibold text-slate-800">
                        {t("hr.job_openings.empty_title")}
                      </p>
                      <p className="mt-1 text-sm text-slate-500">
                        {t("hr.job_openings.empty_body")}
                      </p>
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  items.map((item) => (
                    <tr
                      key={item.id}
                      className="cursor-pointer transition hover:bg-slate-50"
                      onClick={() =>
                        navigate(`/dashboard/hr/job-openings/${item.id}`)
                      }
                    >
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">
                          {item.title}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                          {item.employment_type_label || item.employment_type}
                          {websiteModuleActive && item.show_on_website && (
                            <span className="inline-flex items-center gap-1 text-sky-700">
                              <GlobeAltIcon className="h-3.5 w-3.5" />
                              {t("hr.job_openings.on_website")}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {item.location || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                            STATUS_STYLES[item.status] || STATUS_STYLES.draft
                          }`}
                        >
                          {item.status_label ||
                            t(`hr.job_openings.statuses.${item.status}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {item.application_deadline || "—"}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-800">
                        {item.applications_count ?? 0}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/hr/job-openings/${item.id}`);
                          }}
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </DashboardHeader>

      <JobOpeningJsonDialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        onImported={() => load(controller)}
      />

      <PMDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={t("hr.job_openings.new")}
      >
        <Formik
          initialValues={{
            title: "",
            short_description: "",
            location: "",
            employment_type: "full_time",
            status: "draft",
            show_on_website: false,
            positions_count: 1,
            contact_user: null,
            contact_user_id: null,
          }}
          validationSchema={Yup.object({
            title: Yup.string().required(t("hr.job_openings.validation.title")),
          })}
          onSubmit={handleCreate}
        >
          {(formik) => (
            <Form className="mt-4 space-y-4">
              <PMField
                name="title"
                label={t("hr.job_openings.fields.title")}
                {...formik}
              />
              <PMField
                name="short_description"
                label={t("hr.job_openings.fields.short_description")}
                multiline
                rows={3}
                {...formik}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <PMField
                  name="location"
                  label={t("hr.job_openings.fields.location")}
                  {...formik}
                />
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">
                    {t("hr.job_openings.fields.employment_type")}
                  </label>
                  <select
                    name="employment_type"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                    value={formik.values.employment_type}
                    onChange={formik.handleChange}
                  >
                    <option value="full_time">
                      {t("hr.job_openings.employment_types.full_time")}
                    </option>
                    <option value="part_time">
                      {t("hr.job_openings.employment_types.part_time")}
                    </option>
                    <option value="temporary">
                      {t("hr.job_openings.employment_types.temporary")}
                    </option>
                    <option value="internship">
                      {t("hr.job_openings.employment_types.internship")}
                    </option>
                    <option value="freelance">
                      {t("hr.job_openings.employment_types.freelance")}
                    </option>
                  </select>
                </div>
              </div>
              <JobOpeningContactPicker
                value={formik.values.contact_user}
                onChange={(user) => {
                  formik.setFieldValue("contact_user", user);
                  formik.setFieldValue("contact_user_id", contactUserIdFrom(user));
                }}
              />
              {websiteModuleActive && (
                <PMSwitch
                  label={t("hr.job_openings.fields.show_on_website")}
                  name="show_on_website"
                  {...formik}
                />
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                  onClick={() => setDialogOpen(false)}
                >
                  {t("hr.common.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className="rounded-xl bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_24px_-8px_rgba(20,99,199,.6)] disabled:opacity-60"
                >
                  {t("hr.common.create")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </PMDialog>
    </>
  );
};
